import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router";

export type QueryFilterSchema<T extends Record<string, any>> = {
  [K in keyof T]: T[K];
};

export function useQueryFilters<T extends Record<string, any>>(
  defaults: Partial<QueryFilterSchema<T>> = {},
  queryChangeFn?: () => void
) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();

  const filters = useMemo(() => {
    const obj = {} as T;

    // STEP 1: apply defaults
    (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
      obj[key] = defaults[key] as T[keyof T];
    });

    // STEP 2: apply URL params (override defaults)
    params.forEach((value, key) => {
      obj[key as keyof T] = value as T[keyof T];
    });

    return obj;
  }, [params, defaults]);

  const setFilterValue = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      const newParams = new URLSearchParams(params);

      if (value === undefined || value === null || value === "") {
        newParams.delete(String(key));
      } else {
        newParams.set(String(key), String(value));
      }

      setParams(newParams);
    },
    [params, setParams]
  );

  // NEW: Batch update function
  const setFilterValues = useCallback(
    (updates: Partial<T>) => {
      const newParams = new URLSearchParams(params);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      setParams(newParams);
    },
    [params, setParams]
  );

  const clearFilters = useCallback(() => {
    setParams({});
  }, [setParams]);

  useEffect(() => {
    if (queryChangeFn) {
      queryChangeFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return { filters, setFilterValue, setFilterValues, clearFilters, params };
}
